import { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '../../../../core/components/molecules/Typography';
import { useLesson } from '../../hooks/useLesson';
import { useLessonStore } from '../../stores/lessonStore';
import { formatTime } from '../../../../core/commom/formatTime';
import { Colors, ILesson } from '../../../../core/models/disciplines';
import { useNewSubjectStore } from '../../stores/newSubjectStore';
import { useFilterConfigStore } from '../../stores/filterConfig';
import { IResponseFavorite } from '../../hooks/useContentFavorite';
import { useLessonContentStore } from '../../stores/lessonContentStore';
import { useFinishLesson } from '../../../../core/hooks/useFinishLesson';
import getSubjectByFilter from '../../../../core/commom/getSubjectByFilter';
import CardLoader from '../cardLoading';
import ContentCard from '../../../../core/components/organism/ContentCard';
import SectionHeader from '../sectionHeader';
import FilterModal from '../ModalFilter';
import DisciplinesEmptyState from '../EmptyState';

interface ILessonList {
  module: string;
  discipline: Colors;
  url: string;
}

interface LessonSection {
  title: string;
  data: ILesson[];
}

const filterLessonsByType = (lessons: ILesson[], type: string): ILesson[] => {
  return lessons.filter(lesson => lesson.type === type);
};

export default function LessonList({ module, discipline, url }: ILessonList) {
  const navigate = useNavigate();

  const { mutate, isPending } = useLesson();
  const { selectedSubjects } = useNewSubjectStore();
  const { lessons } = useLessonStore();
  const { mutate: finishLesson, isLoading, isSuccess} = useFinishLesson();

  const { resetFilters, type, status, favorites } = useFilterConfigStore()

  const { setAllLessons } = useLessonContentStore();

  const [main, setMain] = useState<ILesson[]>([]);

  useEffect(() => {
    if (lessons?.length > 0) {
      filterLessons();
    }
  }, [lessons, selectedSubjects]);

  useEffect(() => {
    if (module) {
      mutate(module);
    }
  }, []);

  useEffect(() => {
    if (isSuccess && module) {
      mutate(module);
    }
  }, [isLoading]);

  const callbackFav = (data: IResponseFavorite) => {
    if (data.ok && module) {
      mutate(module);
    }
  };

  const filterLessons = useCallback(() => {
    if (!lessons?.length) return;

    let filteredData = [...lessons];
    const subjectsAux = [...selectedSubjects];

    if (subjectsAux?.length > 0) {
      filteredData = filteredData.filter((it) =>
        subjectsAux.includes(it.subjects[0].slug)
      );
    }

    if (type?.length > 0) {
      filteredData = filteredData.filter((it) => type.includes(it.type));
    }

    if (status?.length === 1) {
      if (status.includes('not_played')) {
        filteredData = filteredData.filter((it) => !it.done);
      } else {
        filteredData = filteredData.filter((it) => it.done);
      }
    }

    if (favorites) {
      filteredData = filteredData.filter((it) => it.favorite);
    }

    setMain(filterLessonsByType(filteredData, 'main'));
    setAllLessons(filteredData);
  }, [lessons, selectedSubjects, type, status, favorites]);

  const lessonSections: LessonSection[] = useMemo(() => [
    { title: 'Principal', data: main }
  ], [main]);

  function dispatchSetContentAsDoneEvent(id: string) {
    const setContentAsDone = new CustomEvent('setContentAsDone', {
      detail: {
        contentId: id,
      },
    });
    window.dispatchEvent(setContentAsDone);
  }

  const onFinish = (id: string, slug: string) => {
    if (slug) {
      finishLesson({ slug, body: null });
      dispatchSetContentAsDoneEvent(id);
    }
  };

  if (isPending || !lessons) {
    return (
      <Fragment>
        {Array.from({ length: 10 }).map((_, index) => (
          <CardLoader key={index} />
        ))}
      </Fragment>
    );
  }

  if (main?.length === 0) {
    return (
      <div className='flex flex-col gap-lg'>
        <FilterModal
          title='Filtrar videoaulas'
          onSubmit={filterLessons}
          reset={resetFilters}
          modalName='normal-filter'
          type='videoaula'
        />
        <DisciplinesEmptyState />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-lg'>
      <FilterModal
        title='Filtrar videoaulas'
        onSubmit={filterLessons}
        reset={resetFilters}
        modalName='normal-filter'
        type='videoaula'
      />
      {lessonSections.map((section, index) => (
        <Fragment key={index}>
          <SectionHeader title={section.title}>
            <Typography size='small'>
              {section.data?.length || 0}{' '}
              {section.data?.length === 1 ? 'videoaula' : 'videoaulas'}
            </Typography>
          </SectionHeader>
          {section.data.map((it, i) => (
            <ContentCard
              key={i}
              content='videoaula'
              title={it.title}
              slug={it.slug}
              icon='play_circle'
              done={it.done}
              favorite={it.favorite}
              color={discipline}
              action={() =>
                navigate(
                  url + `/assunto/${it.subjects[0].slug}/videoaula/${it.slug}`
                )
              }
              subject={getSubjectByFilter(it.subjects, selectedSubjects)}
              hasCallback
              onDone={(id: string, slug: string) => onFinish(id, slug)}
              callback={(data) => callbackFav(data)}
              id={it.id}
            >
              <div className='flex w-fit flex-row gap-lg'>
                <Typography
                  size='small'
                  className=' transition delay-100 ease-in-out group-hover:text-card-color-default'
                >
                  {formatTime(it.duration)}
                </Typography>
                <Typography
                  size='small'
                  className=' transition delay-100 ease-in-out group-hover:text-card-color-default'
                >
                  {it.teacher?.length ? `Prof. ${it.teacher[0]?.name}` : ''}
                </Typography>
              </div>
            </ContentCard>
          ))}
        </Fragment>
      ))}
    </div>
  );
}
